import { createApi } from '@reduxjs/toolkit/query/react';

import { authQuery } from './baseQuery';
import type { DataResponse, PaginatedResponse, TableResponse } from 'src/types/common';
import type {
  IGridCustomer,
  ICustomerProfile,
  IDirectDebitSubscriptions,
  ICustomerDirectDebitSubscriptions,
  IServiceLine,
} from 'src/types/customers';
import type { IBaseServiceLine } from 'src/types/serviceLines';
import { ServiceLineStatus } from 'src/types/serviceLines';

import { IPaginationState, convertObjToUrl } from 'src/utils/server-side-pagination';

export const customersAPISlice = createApi({
  reducerPath: 'presentation-service-customers-api',
  tagTypes: ['Customers'],
  baseQuery: authQuery,
  endpoints: (builder) => ({
    getCustomers: builder.query<
      PaginatedResponse<IGridCustomer>,
      { state: IPaginationState; limit: number }
    >({
      query: ({ state, limit }) => `/customers?limit=${limit}${convertObjToUrl(state)}`,
    }),
    exportCustomers: builder.mutation<{ data: { id: string } }, { state: IPaginationState }>({
      query: ({ state }) => ({
        // limit will be ignored, but I've included it for the sake of parity
        url: `/customers/generate-export?limit=10${convertObjToUrl(state)}`,
        method: 'POST',
      }),
    }),

    getCustomer: builder.query<ICustomerProfile, string>({
      query: (id) => `/customers/${id}`,
      transformResponse: (res: ICustomerProfile) => {
        res.invoices = res.invoices.map((x: any) => ({
          ...x,
          status: x.status,
          stage: x.stage,
          paymentStatus: x.paymentStatus,
          totalRemainingBalance: parseFloat(x.totalRemainingBalance),
          totalAmount: parseFloat(x.totalAmount),
        }));

        return res;
      },
    }),

    getCustomersByClientId: builder.query<TableResponse<ICustomerProfile>, string>({
      query: (clientId) =>
        `/customers?page=1&limit=5000&sort=createdAt&order=desc&clientId=${clientId}`,
    }),

    getCustomerServiceLine: builder.query<DataResponse<IServiceLine>, string>({
      query: (id) => `/service-lines/get-service-line-with-calculation-strategy/${id}`,
    }),

    getServiceLinesList: builder.query<DataResponse<IServiceLine[]>, void>({
      query: () => `/service-lines/get-non-paginated-service-lines`,
    }),

    getServiceLinesPaginated: builder.query<
      PaginatedResponse<IBaseServiceLine>,
      { state: IPaginationState; limit: number }
    >({
      query: ({ state, limit }) => `/service-lines?limit=${limit}${convertObjToUrl(state)}`,
      transformResponse: (res: PaginatedResponse<IServiceLine>) => ({
        ...res,
        data: res.data.map((serviceLine) => {
          const activeRemindersPause = serviceLine.activeRemindersPause ?? null;
          const activeDcPause = serviceLine.activeDcPause ?? null;
          const hasActivePause = activeRemindersPause !== null || activeDcPause !== null;

          return {
            id: serviceLine.id,
            serviceLineId: serviceLine.id,
            serviceLineName: serviceLine.name,
            status: hasActivePause ? ServiceLineStatus.ActivePause : ServiceLineStatus.Active,
            activeRemindersPause,
            activeDcPause,
            pausePeriods: serviceLine.pausePeriods ?? [],
          };
        }),
      }),
    }),

    /**
     * Direct debit subscriptions
     */
    getDirectDebitSubscription: builder.query<
      PaginatedResponse<IDirectDebitSubscriptions>,
      { state: IPaginationState; limit: number }
    >({
      query: ({ limit, state }) =>
        `/customers/direct-debit-subscriptions?limit=${limit}${convertObjToUrl(state)}`,
      providesTags: ['Customers'],
    }),
    /**
     * Direct debit subscriptions for customer
     */

    getCustomerDirectDebitSubscriptions: builder.query<
      ICustomerDirectDebitSubscriptions,
      { customerId: string; page: number }
    >({
      query: ({ customerId, page }) =>
        `/customers/customer-direct-debit-subscriptions/${customerId}?order=desc&sort=createdAt&limit=10&page=${page}`,
    }),
    getWorkflowsSettings: builder.query<{ [key: string]: any }, { id: string }>({
      query: ({ id }) => `/customers/${id}/workflows-settings`,
    }),
  }),
});

export const {
  useGetCustomerQuery,
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetCustomersByClientIdQuery,
  useGetCustomerServiceLineQuery,
  useGetDirectDebitSubscriptionQuery,
  useGetCustomerDirectDebitSubscriptionsQuery,
  useLazyGetCustomerDirectDebitSubscriptionsQuery,
  useGetServiceLinesListQuery,
  useGetServiceLinesPaginatedQuery,
  useExportCustomersMutation,
  useGetWorkflowsSettingsQuery,
} = customersAPISlice;
